import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMasterOverrideMedia,
  createProduct,
  deleteMasterOverrideMedia,
  deleteProduct,
  deleteMediaMasterOverride,
  deleteProductMedia,
  getActivationBlocks,
  getProduct,
  getProductExtendedContracts,
  getProductResolvedAiConfigs,
  getProductSchema,
  hideProductMedia,
  listAttributeValues,
  listProductAreas,
  listProductSources,
  listProductVersionCounts,
  listProductVersions,
  listProducts,
  replaceProductMedia,
  unhideProductMedia,
  updateProduct,
  updateProductMedia,
  uploadProductMedia,
  type ProductMediaUploadMeta,
  type ProductQueryResult,
  type ProductVersionsPage,
  type ProductVersionsQuery,
} from './api';
import { productsKeys } from './query-keys';
import { buildReleasePatch } from './release-to-source';
import {
  type ActivationBlocksResponse,
  type Media,
  type MediaUpdate,
  type Product,
  type AttributeValues,
  type ProductArea,
  type ProductCreate,
  type ProductList,
  type ExtendedContractsResponse,
  type ProductListParams,
  type ProductSchemaResponse,
  type ProductSourceEntry,
  type ResolvedAiConfig,
  type ProductSourcesParams,
  type ProductUpdate,
  type SourceCatalogEntry,
  type ProductVersionCount,
} from './schemas';
import { type ApiError } from '@/lib/api-error';
import { toast } from '@/lib/toast';
import { useAreaStore } from '@/stores/area';

// Listado paginado con filtros. La queryKey incluye los params para que
// TanStack Query cachee por combinación de filtros.
//
// El área activa (store global) entra en la request como `body.area`. Cuando
// hay área, el back ignora `default+schema` y aplica el fieldPriorityConfig
// del cliente — dos pantallas ven lo mismo. La key incluye el slug para no
// cruzar caches entre áreas.
export function useProducts(params: ProductListParams) {
  const area = useAreaStore((s) => s.activeAreaSlug);
  return useQuery<ProductList, ApiError>({
    queryKey: productsKeys.list(params, area),
    queryFn: () => listProducts(params, { area: area ?? undefined }),
    placeholderData: (prev) => prev,
  });
}

// Catálogo de áreas para el selector global. Cambia poco → staleTime largo.
// Tolerante a fallo: si el endpoint no existe todavía (back sin deployar) el
// `retry: false` evita el spam de reintentos; los callers deben renderizar
// "sin áreas configuradas" si `data` viene vacío.
export function useProductAreas() {
  return useQuery<ProductArea[], ApiError>({
    queryKey: productsKeys.areas(),
    queryFn: () => listProductAreas(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// Valores que toma un atributo, para el select de su filtro en el listado.
//
// Se pide SIN `isActive` ni `isWebActive` a propósito: la lista de valores es
// la misma con cualquier estado, sale más rápido (~1 s contra 3-6 s con el
// filtro de ecommerce, medido en producción el 2026-10-05) y queda cacheada 10
// minutos. Pedirla con los estados del listado daría el conteo exacto de cada
// valor, a cambio de dos consultas pesadas más por cada cambio de filtro, en
// paralelo con la del listado.
//
// 400, 403 y 422 (el área activa no lee el campo) no se reintentan: no cambian
// solos.
export function useAttributeValues(pointer: string) {
  const area = useAreaStore((s) => s.activeAreaSlug);
  return useQuery<AttributeValues, ApiError>({
    queryKey: productsKeys.attributeValues(pointer, area),
    queryFn: () => listAttributeValues(pointer, { area: area ?? undefined }),
    staleTime: 10 * 60 * 1000,
    retry: (failureCount, err) => {
      if ([400, 403, 422].includes(err.statusCode)) return false;
      return failureCount < 1;
    },
  });
}

// Detalle por SKU + fuente opcional. El sourceSlug entra en la queryKey para
// cachear por (sku, fuente) — distintas fuentes del mismo SKU pueden tener
// payloads distintos.
//
// `includePendings` (admin-only) adjunta el sidecar de curaduría para el
// PIM. La key incluye el flag: llamar con y sin pendings cachea por separado.
export interface UseProductOptions {
  sourceSlug?: string;
  includePendings?: boolean;
  // Override del área global (poco común — el caso normal es tomarla del
  // store). Se expone para pantallas que necesitan pedir el mismo producto
  // en dos áreas simultáneamente sin cruzar caches.
  area?: string | null;
}

export function useProduct(sku: string | undefined, optionsOrSlug?: string | UseProductOptions) {
  const globalArea = useAreaStore((s) => s.activeAreaSlug);
  const opts: UseProductOptions =
    typeof optionsOrSlug === 'string' ? { sourceSlug: optionsOrSlug } : (optionsOrSlug ?? {});
  // `undefined` en opts.area = usar el área global; `null` explícito =
  // forzar sin área (útil para leer master crudo).
  const effectiveArea = opts.area === undefined ? globalArea : opts.area;
  return useQuery<ProductQueryResult, ApiError>({
    queryKey: productsKeys.detail(sku ?? '', opts.sourceSlug, opts.includePendings, effectiveArea),
    queryFn: () =>
      getProduct(sku as string, {
        sourceSlug: opts.sourceSlug,
        includePendings: opts.includePendings,
        area: effectiveArea ?? undefined,
      }),
    enabled: typeof sku === 'string' && sku.length > 0,
  });
}

// Campos del producto que una regla de activación apagó, con el motivo
// (GET /activation-rules/blocks/:sku). La ficha los muestra de solo lectura.
//
// Un error (403 sin permiso, red, respuesta que no parsea) se trata como "sin
// apagados" y no muestra aviso: el que llama lee `data?.blocks ?? []`. El
// formulario igual manda solo lo que se tocó, y si el back rechaza el guardado
// con el 409, el aviso sale de ahí. 403 no se reintenta: no cambia solo.
export function useActivationBlocks(sku: string | undefined) {
  return useQuery<ActivationBlocksResponse, ApiError>({
    queryKey: productsKeys.activationBlocks(sku ?? ''),
    queryFn: () => getActivationBlocks(sku as string),
    enabled: typeof sku === 'string' && sku.length > 0,
    retry: (failureCount, err) => {
      if (err.statusCode === 403) return false;
      return failureCount < 1;
    },
  });
}

// Schema dinámico de campos editables. Cambia raramente — staleTime largo.
export function useProductSchema() {
  return useQuery<ProductSchemaResponse, ApiError>({
    queryKey: productsKeys.productSchema(),
    queryFn: () => getProductSchema(),
    staleTime: 5 * 60 * 1000,
  });
}

// Contratos tipados de extendedProperties. Cambian solo con redeploy del back
// (bump de schemaVersion) — 1h de staleTime es suficiente para edición real.
export function useProductExtendedContracts() {
  return useQuery<ExtendedContractsResponse, ApiError>({
    queryKey: productsKeys.productExtendedContracts(),
    queryFn: () => getProductExtendedContracts(),
    staleTime: 60 * 60 * 1000,
  });
}

// AI configs resueltos para un producto específico. El back computa qué
// configs aplican siguiendo categoría exacta → ancestros → global. Si el
// productId está vacío, no fetcheamos.
export function useProductResolvedAiConfigs(productId: string | undefined) {
  return useQuery<ResolvedAiConfig[], ApiError>({
    queryKey: productsKeys.resolvedAiConfigs(productId ?? ''),
    queryFn: () => getProductResolvedAiConfigs(productId as string),
    enabled: typeof productId === 'string' && productId.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// Catálogo de fuentes activas (GET /admin/products/sources). Usado para
// poblar el filtro multi-source. Cambia poco → staleTime largo.
//
// Params filtra el catalog server-side. Caso típico:
// - ProductFilters / AiConfig: { canWrite: true, isActive: true } (productores)
// - WebhookDeliveries Cliente: { canRead: true } (consumers)
export function useProductSources(params: ProductSourcesParams = {}) {
  return useQuery<ProductSourceEntry[], ApiError>({
    queryKey: productsKeys.sources(params),
    queryFn: () => listProductSources(params),
    staleTime: 5 * 60 * 1000,
  });
}

// Selector derivado: extrae el catálogo de fuentes (axioma/master/sap…) del
// mismo cache que `useProductSchema`. El backend lo entrega como parte del
// schema response.
export function useSourceCatalog() {
  return useQuery<ProductSchemaResponse, ApiError, SourceCatalogEntry[]>({
    queryKey: productsKeys.productSchema(),
    queryFn: () => getProductSchema(),
    staleTime: 5 * 60 * 1000,
    select: (data) => data.sourceCatalog ?? [],
  });
}

// Página de versiones de un producto. `query.page`/`query.limit` habilitan
// la paginación de la pestaña Versiones (Parte 3); sin ellos, el back
// devuelve su página por default. `enabled` corta el fetch mientras el sku
// todavía no está resuelto (mismo patrón que `useProduct`).
export function useProductVersions(sku: string | undefined, query: ProductVersionsQuery = {}) {
  const { sourceSlug, page, limit } = query;
  return useQuery<ProductVersionsPage, ApiError>({
    queryKey: productsKeys.versions(sku ?? '', sourceSlug, page, limit),
    queryFn: () => listProductVersions(sku as string, query),
    enabled: typeof sku === 'string' && sku.length > 0,
  });
}

// Conteo de versiones por fuente, para el selector de la pestaña Versiones
// (ej: "sap (351)", "vtex (10)").
//
// Un solo request a `/versions/counts`, que el back expone desde la revisión
// hub-service-00228. Antes esto era un pedido por fuente con `limit=1` para
// leer el total del envelope: andaba, pero traía una versión entera por fuente
// solo para contarla, y no sabía de fuentes sin row.
//
// El back devuelve una fila por fuente CON row del producto, incluidas las que
// tienen cero versiones. Una fuente del catálogo que no aparece en la respuesta
// no tiene row para este SKU: se muestra igual, en 0, para que nadie se
// pregunte dónde quedó.
export interface ProductVersionsSourceCount {
  slug: string;
  // `undefined` mientras carga. Un número real después — 0 incluido.
  total: number | undefined;
  // Fecha del último cambio de esa fuente, cuando el back la informa.
  lastChangeAt?: Date | null;
  isError: boolean;
}

export function useProductVersionsCounts(
  sku: string | undefined,
  sources: readonly { slug: string }[],
): ProductVersionsSourceCount[] {
  const query = useQuery<ProductVersionCount[], ApiError>({
    queryKey: productsKeys.versionCounts(sku ?? ''),
    queryFn: () => listProductVersionCounts(sku as string),
    enabled: typeof sku === 'string' && sku.length > 0,
    staleTime: 30_000,
  });

  const bySlug = new Map((query.data ?? []).map((row) => [row.sourceSlug, row] as const));

  return sources.map((source) => {
    const row = bySlug.get(source.slug);
    return {
      slug: source.slug,
      // Sin fila en la respuesta: la fuente no tiene row para este SKU, así
      // que son cero versiones, no un dato faltante.
      total: query.data === undefined ? undefined : (row?.versions ?? 0),
      lastChangeAt: row?.lastChangeAt ?? null,
      isError: query.isError,
    };
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation<Product, ApiError, ProductCreate>({
    mutationFn: (body) => createProduct(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      toast.success('Producto creado');
    },
    onError: (err) => toast.apiError('No se pudo crear el producto', err),
  });
}

// Update tolera pasar `area` explícito en variables; si no viene, cae al
// área activa del store. Motivo: la mutation vive en un componente que ya
// tiene `useProduct(sku, { area })` — si el area es null (sin área) el back
// no lo requiere y el patch va a master directo. Si hay área, va con `?area=`
// para que el back valide editabilidad antes de tocar master.
export function useUpdateProduct() {
  const qc = useQueryClient();
  const globalArea = useAreaStore((s) => s.activeAreaSlug);
  return useMutation<Product, ApiError, { sku: string; body: ProductUpdate; area?: string | null }>(
    {
      mutationFn: ({ sku, body, area }) => {
        const effective = area === undefined ? globalArea : area;
        return updateProduct(sku, body, { area: effective ?? undefined });
      },
      onSuccess: (data, variables) => {
        void qc.invalidateQueries({ queryKey: productsKeys.lists() });
        void qc.invalidateQueries({
          queryKey: productsKeys.detailBySku(variables.sku),
        });
        // Cambió el producto → versions también. La key va por SKU, que es lo
        // que el endpoint busca.
        void qc.invalidateQueries({
          queryKey: productsKeys.versionsBySku(data.sku),
        });
        // El conteo por fuente del selector también cambia: cada cambio real en
        // master escribe una versión, así que el número de al lado de "master"
        // queda viejo si no se invalida.
        void qc.invalidateQueries({
          queryKey: productsKeys.versionCounts(data.sku),
        });
        // Guardar `false` sobre un campo apagado por regla lo vuelve manual, y
        // sin reglas vigentes un `true` borra el apagado viejo: los motivos que
        // muestra la ficha pueden haber cambiado.
        void qc.invalidateQueries({
          queryKey: productsKeys.activationBlocks(variables.sku),
        });
        toast.success('Producto actualizado');
      },
      onError: (err) => toast.apiError('No se pudo actualizar el producto', err),
    },
  );
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (sku) => deleteProduct(sku),
    onSuccess: (_data, sku) => {
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      qc.removeQueries({ queryKey: productsKeys.detailBySku(sku) });
      toast.success('Producto eliminado');
    },
    onError: (err) => toast.apiError('No se pudo eliminar el producto', err),
  });
}

export function useUploadProductMedia() {
  const qc = useQueryClient();
  const globalArea = useAreaStore((s) => s.activeAreaSlug);
  return useMutation<
    Media,
    ApiError,
    {
      sku: string;
      file: File;
      meta?: ProductMediaUploadMeta;
      area?: string | null;
    }
  >({
    mutationFn: ({ sku, file, meta, area }) => {
      const effective = area === undefined ? globalArea : area;
      return uploadProductMedia(sku, file, meta, {
        area: effective ?? undefined,
      });
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: productsKeys.detailBySku(variables.sku),
      });
      toast.success('Imagen subida');
    },
    onError: (err) => toast.apiError('No se pudo subir la imagen', err),
  });
}

export function useReplaceProductMedia() {
  const qc = useQueryClient();
  const globalArea = useAreaStore((s) => s.activeAreaSlug);
  return useMutation<
    Media,
    ApiError,
    { sku: string; mediaId: string; file: File; area?: string | null }
  >({
    mutationFn: ({ sku, mediaId, file, area }) => {
      const effective = area === undefined ? globalArea : area;
      return replaceProductMedia(sku, mediaId, file, {
        area: effective ?? undefined,
      });
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: productsKeys.detailBySku(variables.sku),
      });
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      toast.success('Imagen reemplazada');
    },
    onError: (err) => toast.apiError('No se pudo reemplazar la imagen', err),
  });
}

// "Volver a la fuente": borra el valor de master de un campo mandando `null`
// en el PATCH de siempre. La respuesta 200 es la FILA DE MASTER, no el producto
// consolidado, así que el valor nuevo y su nueva fuente solo se ven tras
// refrescar el detalle — de ahí la invalidación explícita.
//
// Las versiones también cambian: desde hub-service-00228 cada release escribe
// una versión de master con `master-value-released:<pointers>`.
export function useReleaseProductField(sku: string) {
  const qc = useQueryClient();
  const globalArea = useAreaStore((s) => s.activeAreaSlug);
  return useMutation<unknown, ApiError, { pointer: string; label: string }>({
    mutationFn: ({ pointer }) =>
      updateProduct(sku, buildReleasePatch(pointer) as ProductUpdate, {
        area: globalArea ?? undefined,
      }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: productsKeys.detailBySku(sku) });
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      void qc.invalidateQueries({ queryKey: productsKeys.versionsBySku(sku) });
      void qc.invalidateQueries({ queryKey: productsKeys.versionCounts(sku) });
      void qc.invalidateQueries({ queryKey: productsKeys.activationBlocks(sku) });
      toast.success(`${variables.label} volvió a la fuente`);
    },
    onError: (err) => toast.apiError('No se pudo devolver el campo a la fuente', err),
  });
}

export function useUpdateProductMedia() {
  const qc = useQueryClient();
  const globalArea = useAreaStore((s) => s.activeAreaSlug);
  return useMutation<
    Media,
    ApiError,
    {
      sku: string;
      mediaId: string;
      body: MediaUpdate;
      area?: string | null;
    }
  >({
    mutationFn: ({ sku, mediaId, body, area }) => {
      const effective = area === undefined ? globalArea : area;
      return updateProductMedia(sku, mediaId, body, {
        area: effective ?? undefined,
      });
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: productsKeys.detailBySku(variables.sku),
      });
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      toast.success('Imagen actualizada');
    },
    onError: (err) => toast.apiError('No se pudo actualizar la imagen', err),
  });
}

export function useDeleteProductMedia() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { sku: string; mediaId: string }>({
    mutationFn: ({ sku, mediaId }) => deleteProductMedia(sku, mediaId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: productsKeys.detailBySku(variables.sku),
      });
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      toast.success('Imagen eliminada');
    },
    onError: (err) => toast.apiError('No se pudo eliminar la imagen', err),
  });
}

// Revierte un master override sobre una imagen de source (release b7054cb).
// El source row queda intacto; la imagen vuelve a lo que el source publica.
export function useDeleteMediaMasterOverride() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { sku: string; sourceMediaId: string }>({
    mutationFn: ({ sku, sourceMediaId }) => deleteMediaMasterOverride(sku, sourceMediaId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: productsKeys.detailBySku(variables.sku),
      });
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      toast.success('Override revertido');
    },
    onError: (err) => toast.apiError('No se pudo revertir el override', err),
  });
}

// Master override (tipo A): admin sube imagen propia que tapa la imagen de
// la fuente. La fuente NO se borra; se vincula via MediaLink.
export function useCreateMasterOverrideMedia() {
  const qc = useQueryClient();
  const globalArea = useAreaStore((s) => s.activeAreaSlug);
  return useMutation<
    Media,
    ApiError,
    {
      sku: string;
      sourceMediaId: string;
      file: File;
      meta?: ProductMediaUploadMeta;
      area?: string | null;
    }
  >({
    mutationFn: ({ sku, sourceMediaId, file, meta, area }) => {
      const effective = area === undefined ? globalArea : area;
      return createMasterOverrideMedia(sku, sourceMediaId, file, meta, {
        area: effective ?? undefined,
      });
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: productsKeys.detailBySku(variables.sku),
      });
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      toast.success('Override de master subido');
    },
    onError: (err) => toast.apiError('No se pudo subir el override', err),
  });
}

// Oculta una imagen de source para el area activa (release
// hub-service-00157-vmp / client-gateway-00152-87b). El source sigue
// publicando la imagen; solo desaparece del render de esa area. El area
// se toma del store global salvo que el caller la pise en variables.
export function useHideProductMedia() {
  const qc = useQueryClient();
  const globalArea = useAreaStore((s) => s.activeAreaSlug);
  return useMutation<Media, ApiError, { sku: string; sourceMediaId: string; area?: string | null }>(
    {
      mutationFn: ({ sku, sourceMediaId, area }) => {
        const effective = area === undefined ? globalArea : area;
        return hideProductMedia(sku, sourceMediaId, {
          area: effective ?? undefined,
        });
      },
      onSuccess: (_data, variables) => {
        void qc.invalidateQueries({
          queryKey: productsKeys.detailBySku(variables.sku),
        });
        void qc.invalidateQueries({ queryKey: productsKeys.lists() });
        toast.success('Imagen ocultada');
      },
      onError: (err) => toast.apiError('No se pudo ocultar la imagen', err),
    },
  );
}

// Vuelve a mostrar una imagen previamente ocultada por admin en el area
// activa. Idempotente contra unhide sobre algo no oculto.
export function useUnhideProductMedia() {
  const qc = useQueryClient();
  const globalArea = useAreaStore((s) => s.activeAreaSlug);
  return useMutation<Media, ApiError, { sku: string; sourceMediaId: string; area?: string | null }>(
    {
      mutationFn: ({ sku, sourceMediaId, area }) => {
        const effective = area === undefined ? globalArea : area;
        return unhideProductMedia(sku, sourceMediaId, {
          area: effective ?? undefined,
        });
      },
      onSuccess: (_data, variables) => {
        void qc.invalidateQueries({
          queryKey: productsKeys.detailBySku(variables.sku),
        });
        void qc.invalidateQueries({ queryKey: productsKeys.lists() });
        toast.success('Imagen visible');
      },
      onError: (err) => toast.apiError('No se pudo mostrar la imagen', err),
    },
  );
}

// Borra el override master de una source-media. Cascade: row + link + GCS.
export function useDeleteMasterOverrideMedia() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { sku: string; sourceMediaId: string }>({
    mutationFn: ({ sku, sourceMediaId }) => deleteMasterOverrideMedia(sku, sourceMediaId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: productsKeys.detailBySku(variables.sku),
      });
      void qc.invalidateQueries({ queryKey: productsKeys.lists() });
      toast.success('Override eliminado');
    },
    onError: (err) => toast.apiError('No se pudo eliminar el override', err),
  });
}
